/**
 * ChannelQuickEditTab Component
 *
 * Combined view for editing channel-specific images and content.
 * Shows integration source and allows editing both at once.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductChannelPackages } from '@/hooks/useChannelPackage';
import { Alert, Badge } from '@/components/v2/ui';
import { Building2, Eye, Edit } from 'lucide-react';
import type { ChannelPackage } from '@/types/channel-packages';
import { getSourceTypeIcon, getSourceTypeLabel } from '@/types/content-blocks';
import { ChannelPackagePreviewModal, ChannelPackageEditModal } from '@/components/channel-packages';
import { getAssetThumbnailUrl } from '@/lib/helpers/asset-urls';

interface ChannelQuickEditTabProps {
  productId: string;
}

export function ChannelQuickEditTab({ productId }: ChannelQuickEditTabProps) {
  const { t } = useTranslation();
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [previewPackage, setPreviewPackage] = useState<ChannelPackage | null>(null);
  const [editPackage, setEditPackage] = useState<ChannelPackage | null>(null);

  // Load channel packages
  const packagesQuery = useProductChannelPackages(productId);

  if (packagesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (packagesQuery.isError) {
    return (
      <Alert variant="error">
        {t('errors.loading')}
      </Alert>
    );
  }

  const packages = packagesQuery.data?.channel_packages || [];

  if (packages.length === 0) {
    return (
      <Alert variant="info">
        {t('products.noChannelPackages')}
      </Alert>
    );
  }

  const selectedPackage = packages.find(pkg => pkg.id === selectedPackageId) || packages[0];

  return (
    <div className="channel-quick-edit-tab space-y-6">
      {/* Header: Channel Selector */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 mb-3">
          {t('products.selectChannel')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {packages.map((pkg) => {
            const isSelected = pkg.id === selectedPackage.id;
            const integration = pkg.channel?.workspace_integrations;
            const sourceType = pkg.current_version?.source_type;

            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackageId(pkg.id)}
                className={`
                  text-left p-4 rounded-lg border-2 transition-all
                  ${isSelected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                    : 'border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-600 dark:border-neutral-700'
                  }
                `}
              >
                {/* Channel Name */}
                <div className="font-semibold text-neutral-900 dark:text-neutral-50 mb-1">
                  {pkg.channel?.name || 'Unbekannter Channel'}
                </div>

                {/* Integration Badge */}
                {integration && (
                  <div className="flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400 mb-2">
                    <Building2 className="w-3.5 h-3.5" />
                    <span>{integration.name}</span>
                    <Badge size="sm" variant="secondary">
                      {integration.integration_type}
                    </Badge>
                  </div>
                )}

                {/* Source Info */}
                {sourceType && (
                  <div className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                    <span>{getSourceTypeIcon(sourceType)}</span>
                    <span>{getSourceTypeLabel(sourceType)}</span>
                  </div>
                )}

                {/* Version Info */}
                <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  <span>v.{pkg.current_version?.version_number || 0}</span>
                  {pkg.current_version?.asset_renditions && (
                    <>
                      <span>•</span>
                      <span>{pkg.current_version.asset_renditions.length} Bilder</span>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content: Selected Channel Details */}
      {selectedPackage && (
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                {selectedPackage.channel?.name}
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                {selectedPackage.channel?.channel_type}
                {selectedPackage.channel?.workspace_integrations && (
                  <> • {selectedPackage.channel.workspace_integrations.name}</>
                )}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setPreviewPackage(selectedPackage)}
                className="px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                {t('common.preview')}
              </button>
              <button
                onClick={() => setEditPackage(selectedPackage)}
                className="px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                {t('common.edit')}
              </button>
            </div>
          </div>

          {/* Images Preview */}
          {selectedPackage.current_version?.asset_renditions && selectedPackage.current_version.asset_renditions.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                Bilder ({selectedPackage.current_version.asset_renditions.length})
              </h4>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {selectedPackage.current_version.asset_renditions.map((rendition, idx) => (
                  <div
                    key={rendition.asset_id || idx}
                    className="aspect-square rounded border border-neutral-200 dark:border-neutral-800 overflow-hidden bg-neutral-100 dark:bg-neutral-800 relative"
                  >
                    <img
                      src={getAssetThumbnailUrl(rendition)}
                      alt={rendition.alt_text || `Slot ${rendition.slot}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1 left-1 bg-black/75 text-white px-1.5 py-0.5 rounded text-xs font-semibold">
                      #{rendition.slot}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Preview */}
          {selectedPackage.current_version?.content && (
            <div>
              <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                Content
              </h4>
              <div className="space-y-3 text-sm">
                {selectedPackage.current_version.content.title && (
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400 font-medium">Titel:</span>
                    <p className="text-neutral-900 dark:text-neutral-50 mt-0.5">
                      {selectedPackage.current_version.content.title}
                    </p>
                  </div>
                )}
                {selectedPackage.current_version.content.description && (
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400 font-medium">Beschreibung:</span>
                    <p className="text-neutral-900 dark:text-neutral-50 mt-0.5 line-clamp-3">
                      {selectedPackage.current_version.content.description}
                    </p>
                  </div>
                )}
                {selectedPackage.current_version.content.bullets && selectedPackage.current_version.content.bullets.length > 0 && (
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400 font-medium">Bullets:</span>
                    <ul className="list-disc list-inside text-neutral-900 dark:text-neutral-50 mt-0.5">
                      {selectedPackage.current_version.content.bullets.map((bullet, idx) => (
                        <li key={idx}>{bullet}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Source Info Footer */}
          {selectedPackage.current_version && (
            <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                <div className="flex items-center gap-2">
                  {selectedPackage.current_version.source_type && (
                    <>
                      <span>{getSourceTypeIcon(selectedPackage.current_version.source_type)}</span>
                      <span>{getSourceTypeLabel(selectedPackage.current_version.source_type)}</span>
                    </>
                  )}
                </div>
                <div>
                  Version {selectedPackage.current_version.version_number}
                  {selectedPackage.current_version.created_at && (
                    <> • {new Date(selectedPackage.current_version.created_at).toLocaleDateString('de-DE')}</>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      <ChannelPackagePreviewModal
        package_={previewPackage}
        isOpen={!!previewPackage}
        onClose={() => setPreviewPackage(null)}
      />

      {/* Edit Modal */}
      <ChannelPackageEditModal
        package_={editPackage}
        isOpen={!!editPackage}
        onClose={() => setEditPackage(null)}
      />
    </div>
  );
}
